import theme from "../../../../theme";
import { getRGBAFromColor } from "../../../../utils/methods/getCssVariables";

/**
 * Styles for the Questionnaire component
 * @module QuestionnaireSx
 * @description Contains all style objects for the Questionnaire component with exact visual matching
 */

/**
 * Root container styles for the Questionnaire component
 * @type {Object}
 * @property {string} display - Flex display layout
 * @property {string} width - Full width container
 */
export const rootContainer = {
  display: "flex",
  width: "100%"
};

/**
 * Styles for the main content container
 * @type {Object}
 * @property {string} flex - Flex grow property
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} background - White background
 * @property {string} border - 0.0625rem solid border with rgba(0,0,0,0.15)
 * @property {string} borderRadius - 1rem border radius
 * @property {string} margin - Responsive margin
 */
export const contentContainer = {
  flex: "1",
  display: "flex",
  flexDirection: "column",
  background: theme.palette.background.paper,
  margin: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem"
  }
};

/**
 * Styles for the form container
 * @type {Object}
 * @property {string} flex - Flex grow property
 * @property {string} padding - Responsive padding
 * @property {string} maxWidth - No max width restriction
 */
export const formContainer = {
  flex: 1,
  padding: {
    xs: "1rem",
    sm: "1.5rem",
    md: "2rem"
  },
  maxWidth: "none !important"
};

/**
 * Styles for the form element
 * @type {Object}
 * @property {string} height - Full height
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} justifyContent - Space between content
 */
export const formStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between"
};

/**
 * Styles for the questions stack container
 * @type {Object}
 * @property {string} width - Responsive width
 * @property {string} margin - 0 auto for horizontal centering
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} alignItems - Space between alignment
 * @property {string} gap - 1.5rem gap between items
 */
export const questionsStack = {
  width: {
    xs: "100%",
    sm: "95%",
    md: "90%",
    lg: "85%",
    xl: "80%"
  },
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "space-between",
  gap: "1.5rem"
};

/**
 * Styles for the intro text container
 * @type {Object}
 * @property {string} margin - 0.75rem vertical margin
 */
export const introContainer = {
  margin: "0.75rem 0"
};

/**
 * Styles for the main intro text
 * @type {Object}
 * @property {number} fontWeight - Bold (700) weight
 * @property {string} fontSize - 1rem size
 * @property {number} lineHeight - 1.5 line height
 * @property {string} letterSpacing - 0.00938em spacing
 * @property {string} color - Text color with 0.87 opacity from theme
 */
export const introTextMain = {
  fontWeight: 700,
  fontSize: "1rem",
  lineHeight: 1.5,
  letterSpacing: "0.00938em",
  color: getRGBAFromColor({ color: theme.palette.text.primary, alpha: "0.87" })
};

/**
 * Styles for the secondary intro text
 * @type {Object}
 * @property {string} fontSize - 1rem size
 * @property {number} lineHeight - 1.5 line height
 * @property {string} letterSpacing - 0.00938em spacing
 * @property {string} marginTop - 0.25rem top margin
 * @property {string} color - Text color with 0.87 opacity from theme
 */
export const introTextSecondary = {
  fontSize: "1rem",
  lineHeight: 1.5,
  letterSpacing: "0.00938em",
  marginTop: "0.25rem",
  color: getRGBAFromColor({ color: theme.palette.text.primary, alpha: "0.87" })
};

/**
 * Styles for individual question containers
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} flexDirection - Column direction
 * @property {string} gap - 0.25rem gap between elements
 */
export const questionContainer = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem"
};

/**
 * Styles for question rows
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} flexDirection - Responsive direction (column on mobile, row on larger screens)
 * @property {string} justifyContent - Space between alignment
 * @property {string} alignItems - Responsive alignment
 * @property {string} gap - Gap between elements on mobile
 */
export const questionRow = {
  display: "flex",
  flexDirection: {
    xs: "column",
    sm: "row"
  },
  justifyContent: "space-between",
  alignItems: {
    xs: "flex-start",
    sm: "center"
  },
  gap: {
    xs: "0.75rem",
    sm: 0
  }
};

/**
 * Styles for question text
 * @type {Object}
 * @property {string} fontSize - Responsive font size
 * @property {string} color - Text color with 0.87 opacity from theme
 * @property {string} flex - Flex property for responsive layout
 */
export const questionText = {
  fontSize: {
    xs: "0.95rem",
    sm: "1rem"
  },
  color: getRGBAFromColor({ color: theme.palette.text.primary, alpha: "0.87" }),
  flex: {
    xs: "1",
    sm: "0 1 auto"
  }
};

/**
 * Styles for toggle button groups
 * @type {Object}
 * @property {string} mt - Top margin
 * @property {string} width - Responsive width
 */
export const toggleButtonGroup = {
  mt: "0.25rem",
  width: {
    xs: "100%",
    sm: "auto"
  }
};

/**
 * Dynamic styles for toggle buttons
 * @function getToggleButtonStyles
 * @param {string} value - The button value ('1' or '0')
 * @param {string} currentValue - The current selected value
 * @returns {Object} - Style object for the toggle button
 * @property {string} px - Responsive horizontal padding
 * @property {string} flex - Responsive flex property
 * @property {string} backgroundColor - Background color based on selection
 * @property {string} color - Text color based on selection
 */
export const getToggleButtonStyles = (value, currentValue) => ({
  px: {
    xs: 2,
    sm: 3,
    md: 4
  },
  flex: {
    xs: 1,
    sm: "0 1 auto"
  },
  '&.MuiToggleButton-root': {
    backgroundColor: currentValue === value
      ? value === 1
        ? `${theme.palette.primary.main} !important`
        : "#f44336 !important"
      : "transparent !important",
    color: currentValue === value
      ? `${theme.palette.text.secondary} !important`
      : `${theme.palette.text.primary} !important`,
  }
});

/**
 * Styles for error message container
 * @type {Object}
 * @property {string} display - Flex display
 * @property {string} justifyContent - Flex-end alignment
 */
export const errorContainer = {
  display: "flex",
  justifyContent: "flex-end"
};

/**
 * Styles for explanation text field
 * @type {Object}
 * @property {string} mt - 0.75rem top margin (converted from 12px)
 */
export const explanationField = {
  mt: "0.75rem"
};